import { useState, useEffect } from "react";
import BaseModal from "../../BaseComponents/BaseModal/BaseModal";
import {
  useGetHolidayTypes,
  getCountryHolidayTypes,
  assignHolidayTypesToCountry,
} from "../../../api/geo/useHolidayTypes";
import { useToast } from "../../../context/ToastContext";
import styles from "./CountryHolidayTypesModal.module.css";

const CountryHolidayTypesModal = ({
  open,
  onClose,
  country,
  onSave,
}) => {
  const { showToast } = useToast();
  const [holidayTypeIds, setHolidayTypeIds] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);

  const { data: allHolidayTypes = [] } = useGetHolidayTypes({
    page: 1,
    limit: 500,
    languageCode: "ENG",
  });
  const holidayTypeOptions = Array.isArray(allHolidayTypes) ? allHolidayTypes : [];

  useEffect(() => {
    if (!open) return;
    if (country?.id) {
      getCountryHolidayTypes(country.id, { languageCode: "ENG" })
        .then((res) => {
          if (res?.success && Array.isArray(res.data)) {
            setHolidayTypeIds(res.data.map((t) => t.id));
          } else {
            setHolidayTypeIds([]);
          }
        })
        .catch(() => setHolidayTypeIds([]));
    } else {
      setHolidayTypeIds([]);
    }
  }, [open, country?.id]);

  const toggleHolidayType = (typeId) => {
    setHolidayTypeIds((prev) =>
      prev.includes(typeId) ? prev.filter((id) => id !== typeId) : [...prev, typeId]
    );
  };

  const handleSubmit = async () => {
    if (!country?.id) return;
    setSaveLoading(true);
    try {
      await assignHolidayTypesToCountry(country.id, holidayTypeIds);
      onSave?.();
      onClose();
      showToast("Holiday types updated successfully", "success");
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to save holiday types", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  if (!country) return null;

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={`Holiday types for ${country.name}`}
      width="520px"
      footer={
        <>
          <button className={styles.cancelBtn} onClick={onClose} disabled={saveLoading}>
            Cancel
          </button>
          <button
            className={`${styles.primaryBtn} ${saveLoading ? styles.primaryBtnLoading : ""}`}
            onClick={handleSubmit}
            disabled={saveLoading}
          >
            {saveLoading ? (
              <>
                <span className={styles.spinner} aria-hidden />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </button>
        </>
      }
    >
      <div className={styles.content}>
        <p className={styles.hint}>
          Select the holiday types that apply to <strong>{country.name}</strong>.
        </p>
        <div className={styles.checkboxList}>
          <div className={styles.checkboxListHeader}>
            <span className={styles.checkboxListLabel}>Holiday types</span>
            {holidayTypeIds.length > 0 && (
              <span className={styles.selectedBadge}>{holidayTypeIds.length} selected</span>
            )}
          </div>
          {holidayTypeOptions.length === 0 ? (
            <div className={styles.noTypesHint}>No holiday types available. Add types first.</div>
          ) : (
            <div className={styles.checkboxGrid}>
              {holidayTypeOptions.map((type) => (
                <label
                  key={type.id}
                  className={`${styles.checkboxItem} ${holidayTypeIds.includes(type.id) ? styles.checkboxItemSelected : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={holidayTypeIds.includes(type.id)}
                    onChange={() => toggleHolidayType(type.id)}
                  />
                  <span className={styles.typeIcon}>{type.icon || "•"}</span>
                  <span className={styles.typeName}>{type.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </BaseModal>
  );
};

export default CountryHolidayTypesModal;
